/**
 * `.vibgrate/review-context.md` — committed agent memory.
 *
 * An agent reads the repository's instruction files before it writes. This
 * generates a file it can read that carries what Review actually knows: the
 * declared target, the conventions its peers follow, and what is currently
 * open. Commit it, and the next agent starts from the answer instead of
 * rediscovering it — or, more often, not discovering it at all.
 *
 * **It tells the agent to follow the declared target, never "the majority".**
 * That is the whole difference from a consistency scanner's context file. If a
 * repository has declared it is moving to a layered architecture, the memory
 * must say so even while most of the code still does the old thing — otherwise
 * the file becomes an instruction to perpetuate the legacy, and every agent
 * that reads it makes the migration harder.
 *
 * Also emitted as a managed block so `--inject-context` can update it inside
 * an existing CLAUDE.md / AGENTS.md without disturbing anything a human wrote.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import { describePattern } from './dimensions.js';
import type { DominanceVote } from './dominance.js';
import type { ReviewReceipt } from './schemas.js';

export const CONTEXT_PATH = '.vibgrate/review-context.md';
export const BLOCK_START = '<!-- vibgrate:review-context:start -->';
export const BLOCK_END = '<!-- vibgrate:review-context:end -->';

export interface ContextInput {
  receipt: ReviewReceipt;
  votes: readonly DominanceVote[];
  /** The architecture the repository declared, if any. */
  declaredTarget: string | null;
  /** Where that declaration was read from. */
  intentSources: readonly string[];
}

/** How many conventions to list. Beyond this it stops being memory and becomes a report. */
const MAX_CONVENTIONS = 8;
const MAX_OPEN_FINDINGS = 10;

export function renderContext(input: ContextInput): string {
  const { receipt, votes, declaredTarget } = input;
  const findings = [...receipt.findings.architecture_findings, ...receipt.findings.security_findings];
  const lines: string[] = [];

  lines.push('# Review context');
  lines.push('');
  lines.push(
    'Generated by `vg review --write-context`. It records what Vibgrate Review knows '
      + 'about this repository so an agent does not have to rediscover it. Commit it.',
  );
  lines.push('');

  // ── The target ───────────────────────────────────────────────────────────
  lines.push('## The architecture this repository is aiming at');
  lines.push('');
  if (declaredTarget) {
    lines.push(`**Declared target: \`${declaredTarget}\`.**`);
    if (input.intentSources.length > 0) {
      lines.push('');
      lines.push(`Declared in ${input.intentSources.map((s) => `\`${s}\``).join(', ')}.`);
    }
    lines.push('');
    lines.push(
      'Write new code to match this target, **not** to match whatever most of the existing '
        + 'code does. Where the two disagree, the target wins and the existing code is the '
        + 'thing being migrated.',
    );
  } else {
    lines.push('**No target architecture has been declared.**');
    lines.push('');
    lines.push(
      'The conventions below are what the code currently does. They are an observation, '
        + 'not an instruction — they may be exactly what the team is trying to move away from. '
        + 'Set `target_pattern` in `.vibgrate/review.toml` to make the intended architecture '
        + 'explicit, and Review will hold changes to it.',
    );
  }
  lines.push('');

  // ── Observed conventions ────────────────────────────────────────────────
  const decided = votes.filter((v) => v.reason === 'dominant' && v.dominant).slice(0, MAX_CONVENTIONS);
  lines.push('## What the existing code does');
  lines.push('');
  if (decided.length === 0) {
    lines.push('No peer group was consistent enough to establish a convention.');
  } else {
    lines.push('| Peer group | Convention | Share | Follow the example in |');
    lines.push('|---|---|---:|---|');
    for (const vote of decided) {
      lines.push(
        `| \`${vote.group}\` | ${describePattern(vote.dominant!)} | ${(vote.share * 100).toFixed(0)}% `
          + `| ${vote.exemplars.slice(0, 2).map((e) => `\`${e}\``).join(', ') || '—'} |`,
      );
    }
  }
  const noConvention = votes.filter((v) => v.reason === 'no_convention');
  if (noConvention.length > 0) {
    lines.push('');
    lines.push(
      `${noConvention.length} peer group(s) have **no** convention — the patterns are too evenly `
        + 'split for one to be normal. Pick the one that matches the declared target rather than '
        + 'copying the nearest file.',
    );
  }
  lines.push('');

  // ── What is currently open ──────────────────────────────────────────────
  lines.push('## Open findings on the last reviewed change');
  lines.push('');
  lines.push(
    `Decision: \`${receipt.decision}\` · `
      + `${receipt.counts.architecture} architecture · ${receipt.counts.security} security · `
      + `${receipt.counts.protected} protected · at \`${receipt.git.head_sha.slice(0, 8)}\`.`,
  );
  lines.push('');
  if (findings.length === 0) {
    lines.push('None.');
  } else {
    for (const f of findings.slice(0, MAX_OPEN_FINDINGS)) {
      const badge = f.protected_finding ? ' **(protected — cannot be waived)**' : '';
      lines.push(`- \`${f.kind}\`${badge} — ${f.claim} → ${f.remediation}`);
    }
    if (findings.length > MAX_OPEN_FINDINGS) {
      lines.push(`- …and ${findings.length - MAX_OPEN_FINDINGS} more (\`vg review\`).`);
    }
  }
  lines.push('');

  // ── Instructions to the agent ───────────────────────────────────────────
  lines.push('## If you are an AI agent working in this repository');
  lines.push('');
  lines.push(
    declaredTarget
      ? `1. Match the declared \`${declaredTarget}\` architecture above, even where existing code does not.`
      : '1. There is no declared architecture. Match the nearest convention above, and say so in your PR description.',
  );
  lines.push('2. Before writing a new function, check whether one already does the job — `vg review` reports re-implementations.');
  lines.push('3. A new mutating route needs an authorization guard unless the surrounding routes are deliberately public.');
  lines.push('4. Run `vg review` before you finish. A `protected` finding cannot be waived — it must be fixed.');
  lines.push('');
  lines.push(
    '<sub>Review reports change integrity. It does not prove code is secure, and absence of '
      + 'findings is not a certification.</sub>',
  );

  return lines.join('\n');
}

/** Write `.vibgrate/review-context.md`, creating the directory if needed. */
export function writeContextFile(root: string, input: ContextInput): string {
  const target = path.join(root, CONTEXT_PATH);
  fs.mkdirSync(path.dirname(target), { recursive: true });
  fs.writeFileSync(target, `${renderContext(input)}\n`, 'utf8');
  return CONTEXT_PATH;
}

/**
 * Replace (or append) the managed block inside an existing instruction file.
 *
 * Only the text between the markers is ever touched, so a human's own
 * instructions in the same file survive regeneration untouched. A file that has
 * never been injected gets the block appended.
 */
export function injectContextBlock(existing: string, body: string): string {
  const block = `${BLOCK_START}\n${body}\n${BLOCK_END}`;
  const startAt = existing.indexOf(BLOCK_START);
  const endAt = existing.indexOf(BLOCK_END);
  if (startAt !== -1 && endAt !== -1 && endAt > startAt) {
    return existing.slice(0, startAt) + block + existing.slice(endAt + BLOCK_END.length);
  }
  const separator = existing.endsWith('\n') ? '\n' : '\n\n';
  return `${existing}${separator}${block}\n`;
}
